import en from './en.js?v=20260729-breeding-1';
import ptBR from './pt-BR.js?v=20260729-breeding-1';
import es from './es.js?v=20260729-breeding-1';
import { LOOT_I18N_STUBS } from '../generated/loot-i18n-stubs.js';

export const SUPPORTED_LOCALES = Object.freeze([
  { id: 'en', labelKey: 'settings.language.en' },
  { id: 'pt-BR', labelKey: 'settings.language.pt-BR' },
  { id: 'es', labelKey: 'settings.language.es' },
]);

const CATALOGS = Object.freeze({
  en: Object.freeze({ ...LOOT_I18N_STUBS, ...en }),
  'pt-BR': Object.freeze({ ...LOOT_I18N_STUBS, ...ptBR }),
  es: Object.freeze({ ...LOOT_I18N_STUBS, ...es }),
});

const STORAGE_KEY = 'idlemon-locale';
const listeners = new Set();

let currentLocale = 'en';

function normalizeLocale(id) {
  if (id === 'pt' || id === 'pt-br' || id === 'pt_BR') return 'pt-BR';
  if (id === 'es-ES' || id === 'es-MX') return 'es';
  if (CATALOGS[id]) return id;
  return 'en';
}

export function getLocale() {
  return currentLocale;
}

export function t(key, params = {}) {
  const catalog = CATALOGS[currentLocale] || en;
  let text = catalog[key] ?? en[key] ?? key;
  if (params && typeof text === 'string') {
    text = text.replace(/\{(\w+)\}/g, (_, name) => (
      params[name] != null ? String(params[name]) : `{${name}}`
    ));
  }
  return text;
}

/** Repara tipo gravado como mojibake (ex.: "Ã¡gua" → "água"). */
export function normalizeTypeId(typeId) {
  const raw = String(typeId || '').trim();
  if (!raw) return '';
  if (!/[ÃÂ]/.test(raw)) return raw;
  try {
    const bytes = Uint8Array.from([...raw].map((ch) => ch.charCodeAt(0) & 0xff));
    const fixed = new TextDecoder('utf-8').decode(bytes);
    return fixed || raw;
  } catch {
    return raw;
  }
}

export function typeLabel(typeId, { upper = false } = {}) {
  if (!typeId) return '';
  const id = normalizeTypeId(typeId);
  const label = t(`types.${id}`);
  return upper ? label.toUpperCase() : label;
}

export function rarityLabel(rarityName) {
  if (!rarityName) return t('rarities.Comum');
  const key = `rarities.${rarityName}`;
  const catalog = CATALOGS[currentLocale] || en;
  return catalog[key] ?? en[key] ?? rarityName;
}

export function itemName(slug) {
  if (!slug) return '';
  const key = `items.${slug}.name`;
  const catalog = CATALOGS[currentLocale] || en;
  return catalog[key] ?? en[key] ?? slug;
}

export function itemDesc(slug) {
  if (!slug) return '';
  const key = `items.${slug}.desc`;
  const catalog = CATALOGS[currentLocale] || en;
  return catalog[key] ?? en[key] ?? '';
}

export function mapTitle(slug) {
  if (!slug) return '';
  return t(`maps.${slug}`);
}

export function formatNumber(value, options) {
  return Number(value || 0).toLocaleString(currentLocale, options);
}

export function formatDateTime(value, options = { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(currentLocale, options);
}

export function formatTime(value, options = { hour: '2-digit', minute: '2-digit', second: '2-digit' }) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString(currentLocale, options);
}

/** Apply data-i18n* attributes under root (default: document). */
export function applyStaticI18n(root = typeof document !== 'undefined' ? document : null) {
  if (!root) return;
  root.querySelectorAll('[data-i18n]').forEach((el) => {
    const key = el.getAttribute('data-i18n');
    if (!key) return;
    if (el.childElementCount === 0) {
      el.textContent = t(key);
      return;
    }
    // Keep child nodes (e.g. counters); update first text node or dedicated span
    const labeled = el.querySelector('[data-i18n-text]');
    if (labeled) {
      labeled.textContent = t(key);
      return;
    }
    for (const node of el.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
        node.textContent = ` ${t(key)} `;
        return;
      }
    }
  });
  root.querySelectorAll('[data-i18n-placeholder]').forEach((el) => {
    el.setAttribute('placeholder', t(el.getAttribute('data-i18n-placeholder')));
  });
  root.querySelectorAll('[data-i18n-aria-label]').forEach((el) => {
    el.setAttribute('aria-label', t(el.getAttribute('data-i18n-aria-label')));
  });
  root.querySelectorAll('[data-i18n-title]').forEach((el) => {
    el.setAttribute('title', t(el.getAttribute('data-i18n-title')));
  });
  root.querySelectorAll('[data-i18n-alt]').forEach((el) => {
    el.setAttribute('alt', t(el.getAttribute('data-i18n-alt')));
  });
  if (typeof document !== 'undefined') {
    document.documentElement.lang = currentLocale === 'pt-BR' ? 'pt-BR' : currentLocale;
    document.querySelectorAll('.team-pokemon-row.active').forEach((el) => {
      el.setAttribute('data-active-label', t('common.active'));
    });
  }
}

export function onLocaleChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setLocale(localeId, { persist = true } = {}) {
  const next = normalizeLocale(localeId);
  currentLocale = next;
  if (persist) {
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }
  if (typeof document !== 'undefined') applyStaticI18n(document);
  listeners.forEach((fn) => {
    try { fn(next); } catch { /* ignore */ }
  });
  return next;
}

export function initI18n() {
  let saved = 'en';
  try { saved = localStorage.getItem(STORAGE_KEY) || 'en'; } catch { saved = 'en'; }
  return setLocale(saved, { persist: false });
}
